import { blobToBytes } from '../core/blob-bytes';
import { THUMBNAIL_MAX_BYTES } from './history-db';

export const THUMBNAIL_MAX_EDGE = 192;
export const THUMBNAIL_MIME = 'image/jpeg';
export const THUMBNAIL_QUALITY = 0.72;

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

function createCanvas(width: number, height: number): AnyCanvas | null {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }
  return null;
}

async function canvasToDataUrl(canvas: AnyCanvas): Promise<string | null> {
  if (typeof (canvas as OffscreenCanvas).convertToBlob === 'function') {
    const blob = await (canvas as OffscreenCanvas).convertToBlob({ type: THUMBNAIL_MIME, quality: THUMBNAIL_QUALITY });
    if (blob.size > THUMBNAIL_MAX_BYTES) return null;
    const bytes = await blobToBytes(blob);
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return `data:${THUMBNAIL_MIME};base64,${btoa(binary)}`;
  }
  const element = canvas as HTMLCanvasElement;
  const dataUrl = element.toDataURL(THUMBNAIL_MIME, THUMBNAIL_QUALITY);
  return dataUrl.length > THUMBNAIL_MAX_BYTES ? null : dataUrl;
}

/**
 * 生成记录页缩略图。
 * 环境不支持或产物过大时返回 undefined——记录功能不因缩略图失败而中断。
 */
export async function createImageThumbnail(file: File, maxEdge: number = THUMBNAIL_MAX_EDGE): Promise<string | undefined> {
  try {
    if (typeof createImageBitmap !== 'function') return undefined;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = createCanvas(width, height);
    if (!canvas) {
      bitmap.close();
      return undefined;
    }
    const context = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!context) {
      bitmap.close();
      return undefined;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    return (await canvasToDataUrl(canvas)) ?? undefined;
  } catch {
    return undefined;
  }
}
